import { AnalyticsData, LimitCheckAlertStatus, RootState } from "../types";
import { isMilestoneSeasonEnabled } from "../utils/milestoneSeason";

export const getVouchers = (state: RootState) => state.common.vouchers;
export const getIsLoadingVouchers = (state: RootState) =>
  state.common.isLoadingVouchers;
export const getDiscountVoucher = (state: RootState) =>
  state.voucherCampaign.discountVoucher;
export const getCashVoucher = (state: RootState) =>
  state.voucherCampaign.cashVoucher;
export const getGiveaway = (state: RootState) => state.voucherCampaign.giveaway;
export const getSpecialAddress = (state: RootState) =>
  state.voucherCampaign.specialAddress;
export const getCurrency = (state: RootState) => state.voucherCampaign.currency;
export const getEventPass = (state: RootState) =>
  state.voucherCampaign.eventPass;
export const getEventAddress = (state: RootState) =>
  state.voucherCampaign.eventAddress;
export const getName = (state: RootState) => state.user.name;
export const getCurrentSubscription = (state: RootState): number =>
  state.user.currentSubscription;
export const getMerchantUserRole = (state: RootState) =>
  state.user?.merchantUserRole;
export const getSuperAdmin = (state: RootState) => state.user.superAdmin;
export const getEmail = (state: RootState) => state.user.email;
export const getCanCreateOrganization = (state: RootState): boolean =>
  Boolean(state.user.canCreateOrganization);
export const getOrgOptionAllowlistLoaded = (state: RootState): boolean =>
  Boolean(state.user.orgOptionAllowlistLoaded);
export const getId = (state: RootState) => state.user.id;
export const getActualUuid = (state: RootState) => state.user.uuid;
export const getProfileImageId = (state: RootState) =>
  state.user.profileImageId;
export const getUuid = (state: RootState): string | undefined =>
  state.user.impersonatingMerchantId || state.user.uuid;
export const getLimitcheck = (
  state: RootState,
): LimitCheckAlertStatus | undefined => state.user.limitcheck;
export const getAnalytics = (state: RootState): AnalyticsData =>
  state.user.analytics;
export const getOtp = (state: RootState) => state.user.otp;
export const getAccountUuidToLogIn = (state: RootState) =>
  state.user.accountUuidToLogIn;
export const getCheckRestart = (state: RootState) => state.user.checkRestart;
export const getSelectedLp = (state: RootState) => state.common.selected_lp;
export const getSelectedLpUuid = (state: RootState) =>
  state.common.selected_lp_uuid;
export const getLoyaltyProgramDataRevision = (state: RootState) =>
  state.common.loyalty_program_data_revision ?? 0;
export const getImpersonatingMerchantId = (state: RootState) =>
  state.user.impersonatingMerchantId;
export const getSelectedProgramName = (state: RootState) =>
  state.common.selected_program_name;
export const getSelectedProgramLocation = (state: RootState) =>
  state.common.selected_program_location;
export const getBusinessImageName = (state: RootState) =>
  state.common.business_image_name;
// Older sessions may have persisted a non-list API response in Redux.
// Treat it as unloaded until the dashboard's refresh replaces it.
export const getProgram = (state: RootState) =>
  Array.isArray(state.common.program) ? state.common.program : null;
export const getIsLoadingPrograms = (state: RootState) =>
  state.common.isLoadingPrograms;
export const getHasAnyProgram = (state: RootState) =>
  Boolean(getProgram(state)?.length);
export const getIsGoPremiumPopupShown = (state: RootState) =>
  state.common.isGoPremiumPopupShown;
export const getIsLoadingOverviewData = (state: RootState) =>
  state.common.isLoadingOverviewData;
export const getOverviewData = (state: RootState) => state.common.overview_data;
export const getIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const getAuthIsLoading = (state: RootState) => state.auth.isLoading;
export const getAuthError = (state: RootState) => state.auth.error;
export const getIsLoggedOut = (state: RootState) => state.auth.isLoggedOut;
export const getIndustries = (state: RootState) => state.common.industries;
export const getBusinessBrandColor = (state: RootState) =>
  state.common.selected_color;

// Account
export const getAccounts = (state: RootState) => state.common.accounts;
export const getAccountId = (state: RootState) => state.account.id;
export const getAccountUuid = (state: RootState): string | undefined =>
  state.account.uuid;
export const getAccountName = (state: RootState) => state.account.name;
export const SUBSCRIPTION_FREE = 0;
export const SUBSCRIPTION_MONTHLY = 1;
export const SUBSCRIPTION_ANNUAL = 2;
export const SUBSCRIPTION_FREE_TRIAL = 3;

export const isFreeTierSubscription = (
  subscription: number | null | undefined,
): boolean => (subscription ?? SUBSCRIPTION_FREE) === SUBSCRIPTION_FREE;

export const isFreeTrialSubscription = (
  subscription: number | null | undefined,
): boolean => subscription === SUBSCRIPTION_FREE_TRIAL;

/**
 * Free and free-trial accounts share the member limit bar.
 * @param {number | null | undefined} subscription
 * @returns {boolean}
 */
export const showsMemberLimitBar = (
  subscription: number | null | undefined,
): boolean =>
  isFreeTierSubscription(subscription) || isFreeTrialSubscription(subscription);

/**
 * Paid and free-trial accounts can use premium features.
 * @param {number | null | undefined} subscription
 * @returns {boolean}
 */
export const hasPremiumFeatureAccess = (
  subscription: number | null | undefined,
): boolean => !isFreeTierSubscription(subscription);

/** Free to start plans may only run two stamp card seasons; season 3+ requires Pro. */
export const FREE_TIER_MAX_STAMP_CARD_SEASONS = 2;

/**
 * True when the given season number is not allowed on the account subscription.
 * @param {number | null | undefined} subscription - Account subscription tier
 * @param {number} seasonNumber - Parsed season index (1, 2, 3, …)
 * @returns {boolean}
 */
export const isStampCardSeasonRestrictedForSubscription = (
  subscription: number | null | undefined,
  seasonNumber: number,
): boolean =>
  isFreeTierSubscription(subscription) &&
  seasonNumber > FREE_TIER_MAX_STAMP_CARD_SEASONS;

export const getAccountSubscription = (state: RootState) =>
  state.account.subscription;
export const getIsBillingFrozen = (state: RootState): boolean =>
  Boolean(state.account.isBillingFrozen);
export const getAccountTrialStartDate = (state: RootState) =>
  state.account.trialStartDate;
export const getAccountTrialEndDate = (state: RootState) =>
  state.account.trialEndDate;
export const getAccountTrialDays = (state: RootState) =>
  state.account.trialDays;
export const getAccountRoutingKey = (state: RootState): unknown =>
  state.account.routingKey;
export const getIsStampCardSeasonEnabled = (state: RootState): boolean =>
  isMilestoneSeasonEnabled(getAccountRoutingKey(state));
export const getUserAccountRole = (state: RootState) =>
  state.account?.userAccountRole;

const normalizedRoleIsUser = (role: string | null | undefined): boolean =>
  String(role ?? "")
    .trim()
    .toUpperCase() === "USER";

/**
 * True when the user should have read-only portal access (USER role).
 * Checks both account.userAccountRole (Profile) and user.merchantUserRole (login API):
 * setAccount() after login can overwrite the account slice with a wrong nested userRole,
 * while merchantUserRole from the login response stays correct.
 * @param {import("../types").RootState} state Redux root state
 * @returns {boolean} True when the portal should be view-only for the current user
 */
export const getIsViewOnly = (state: RootState): boolean =>
  normalizedRoleIsUser(getUserAccountRole(state)) ||
  normalizedRoleIsUser(state.user?.merchantUserRole);
export const getCountries = (state: RootState) => state.common.countries;
export const getLocations = (state: RootState) => state.programLocation;
export const getIsManualUpgrade = (state: RootState) =>
  state.account.IsManualUpgrade;
export const getCancelPending = (state: RootState) =>
  state.account.cancelPending;
